import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { getAllInstallations } from '../services/api';
import { backupLogAPI } from '../api/backupLog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import './Dashboard.css';
import { backupConfigAPI } from '../api/backupConfig';

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState({
    totalInstallations: 0,
    activeInstallations: 0,
    totalConfigs: 0,
    recentLogs: []
  });
  const [runningBackups, setRunningBackups] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardData();
    const interval = setInterval(fetchRunningBackups, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      
      const installationsRes = await getAllInstallations(1, 100);
      const installations = installationsRes.data || [];
      const configs = await backupConfigAPI.getAll(1,10000);
      const runningRes = await backupLogAPI.getRunningBackups();
      const logsRes = await backupLogAPI.getAll(1, 5);
      
      setStats({
        totalInstallations: installations.length,
        activeInstallations: installations.filter(i => i.isActive).length,
        totalConfigs: configs.data.length,
        recentLogs: logsRes.data || []
      });
      
      setRunningBackups(runningRes.data || []);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const fetchRunningBackups = async () => {
    try {
      const response = await backupLogAPI.getRunningBackups();
      setRunningBackups(response.data || []);
    } catch (error) {
      console.error('Failed to fetch running backups:', error);
    }
  };

  const getTimeSince = (date) => {
    if (!date) return 'N/A';
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    if (seconds < 60) return `${seconds}s ago`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    return `${Math.floor(seconds / 86400)}d ago`;
  };

  const getStatusBadge = (status) => {
    const badges = {
      success: { icon: '✅', text: 'Success', class: 'success' },
      failed: { icon: '❌', text: 'Failed', class: 'failed' },
      in_progress: { icon: '⏳', text: 'Running', class: 'in-progress' },
      initiated: { icon: '🔄', text: 'Starting', class: 'initiated' }
    };
    return badges[status] || { icon: '❓', text: status, class: 'unknown' };
  };

  if (loading) {
    return <div className="loading">Loading dashboard...</div>;
  }

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div>
          <h1>Welcome back, {user?.username || 'Admin'}! 👋</h1>
          <p>Manage your backup installations and configurations</p>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card blue">
          <div className="stat-icon">🏢</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalInstallations}</div>
            <div className="stat-label">Total Installations</div>
          </div>
          <Link to="/installations" className="stat-link">View All →</Link>
        </div>

        <div className="stat-card green">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <div className="stat-value">{stats.activeInstallations}</div>
            <div className="stat-label">Active Installations</div>
          </div>
          <Link to="/installations" className="stat-link">Manage →</Link>
        </div>

        <div className="stat-card purple">
          <div className="stat-icon">⚙️</div>
          <div className="stat-content">
            <div className="stat-value">{stats.totalConfigs}</div>
            <div className="stat-label">Backup Configs</div>
          </div>
          <Link to="/backup-configs" className="stat-link">View All →</Link>
        </div>

        <div className="stat-card orange">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <div className="stat-value">{runningBackups.length}</div>
            <div className="stat-label">Running Backups</div>
          </div>
          {runningBackups.length > 0 && (
            <span className="stat-link">Live now</span>
          )}
        </div>
      </div>

      {runningBackups.length > 0 && (
        <div className="dashboard-section">
          <div className="section-header">
            <h2>🔄 Running Backups</h2>
            <span className="live-indicator">
              <span className="pulse"></span> Live
            </span>
          </div>
          <div className="running-backups-grid">
            {runningBackups.map(backup => {
              const badge = getStatusBadge(backup.status);
              return (
                <div key={backup._id} className="running-backup-card">
                  <div className="backup-header">
                    <div>
                      <h4>{backup.installation?.installationName || 'Unknown'}</h4>
                      <p>{backup.backupConfig?.configName || 'N/A'}</p>
                    </div>
                    <span className={`badge badge-${badge.class}`}>
                      {badge.icon} {badge.text}
                    </span>
                  </div>
                  <div className="backup-footer">
                    <span className="time-info">Started {getTimeSince(backup.startTime)}</span>
                    <Link to={`/backup-log/${backup._id}`} className="btn-link">
                      View Details →
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="dashboard-section">
        <div className="section-header">
          <h2>📊 Recent Backup Logs</h2>
          <Link to="/backup-logs" className="btn btn-sm btn-primary">
            View All Logs
          </Link>
        </div>
        {stats.recentLogs.length > 0 ? (
          <div className="logs-table-wrapper">
            <table className="dashboard-table">
              <thead>
                <tr>
                  <th>Installation</th>
                  <th>Configuration</th>
                  <th>Status</th>
                  <th>Time</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {stats.recentLogs.map(log => {
                  const badge = getStatusBadge(log.status);
                  return (
                    <tr key={log._id}>
                      <td>
                        <div className="table-cell-content">
                          <span className="icon">🏢</span>
                          {log.installation?.installationName || 'N/A'}
                        </div>
                      </td>
                      <td>{log.backupConfig?.configName || 'N/A'}</td>
                      <td>
                        <span className={`badge badge-${badge.class}`}>
                          {badge.icon} {badge.text}
                        </span>
                      </td>
                      <td>{getTimeSince(log.startTime)}</td>
                      <td>
                        <Link to={`/backup-log/${log._id}`} className="btn btn-sm btn-outline">
                          View
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="empty-state">
            <div className="empty-icon">📭</div>
            <p>No recent backup logs</p>
            <span className="empty-subtext">Backup logs will appear here once backups are executed</span>
          </div>
        )}
      </div>

      <div className="dashboard-section">
        <div className="section-header">
          <h2>⚡ Quick Actions</h2>
        </div>
        <div className="quick-actions-grid">
          <Link to="/installations/create" className="action-card">
            <div className="action-icon blue">➕</div>
            <div className="action-content">
              <h3>New Installation</h3>
              <p>Register a new backup installation</p>
            </div>
            <div className="action-arrow">→</div>
          </Link>
          
          <Link to="/backup-configs/create" className="action-card">
            <div className="action-icon green">⚙️</div>
            <div className="action-content">
              <h3>New Backup Config</h3>
              <p>Create a backup configuration</p>
            </div>
            <div className="action-arrow">→</div>
          </Link>
          
          <Link to="/schedules/new" className="action-card">
            <div className="action-icon purple">⏰</div>
            <div className="action-content">
              <h3>New Schedule</h3>
              <p>Define a backup schedule</p>
            </div>
            <div className="action-arrow">→</div>
          </Link>
          
          <Link to="/sftp-servers/new" className="action-card">
            <div className="action-icon orange">🖥️</div>
            <div className="action-content">
              <h3>New SFTP Server</h3>
              <p>Add an SFTP server</p>
            </div>
            <div className="action-arrow">→</div>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;